// GES-403 follow-up: an overloaded API answers a GET with 503 {code:'DB_BUSY', retry_after} (the Retry-After header is not
// readable cross-origin, so the body carries the same seconds). Retry that GET a bounded number of times, then hand the last
// response back so the caller shows ONE clean "database busy" message. GET only: a write is never replayed here.
import { abortError } from './navAbort.mjs';
export const BUSY_RETRIES = 2;        // extra attempts after the first (worst case ~2 x 5 s of waiting)
const MAX_WAIT_MS = 8000;

export async function isDbBusy(response) {
  if (response.status !== 503) return null;
  const body = await response.clone().json().catch(() => null);
  return body && (body.code === 'DB_BUSY' || body.code === 'PGRST_UNHEALTHY') ? body : null;   // perf-reads: PGRST_UNHEALTHY = PostgREST reloading its schema cache
}

// abort-reads: settle with the promise, or reject as AbortError the moment the signal fires (already-aborted rejects at once).
const abortable = (p, signal) => !signal ? p : new Promise((resolve, reject) => {
  if (signal.aborted) return reject(abortError());
  const on = () => reject(abortError());
  signal.addEventListener('abort', on, { once: true });
  p.then(resolve, reject).finally(() => signal.removeEventListener('abort', on));
});

export async function fetchRetryBusy(doFetch, url, init = {}, { sleep = (ms) => new Promise(r => setTimeout(r, ms)), onGiveUp } = {}) {
  const isGet = String(init.method || 'GET').toUpperCase() === 'GET';
  let response = await doFetch(url, init);
  for (let i = 0; isGet && i < BUSY_RETRIES; i++) {
    const busy = await isDbBusy(response);
    if (!busy) break;
    if (init.signal && init.signal.aborted) throw abortError();
    await abortable(sleep(Math.min(MAX_WAIT_MS, Math.max(500, (Number(busy.retry_after) || 5) * 1000))), init.signal);   // abort-reads: leaving the page ends the wait
    response = await doFetch(url, init);
  }
  // still shed after the retries: tell the shell ONCE so it can show the shared banner (pages that swallow errors would otherwise show an empty list)
  if (isGet && onGiveUp && await isDbBusy(response)) onGiveUp();
  return response;
}
